import { useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import Modal from "react-modal";
import ModalStyle from "Assets/ModalStyle";
import ObjectId from "bson-objectid";
import Calendar from "react-calendar";

import PlantModal from "Components/PlantModal";
import DeletePlantingModal from "./DeletePlantingModal";
import { getPlantDisplayName } from "Helpers/PlantName";
import { usePlantStore } from "Store/PlantStore";
import { usePlantingStore } from "Store/PlantingStore";
import { useGerminationTrayStore } from "Store/GerminationTrayStore";
import { useVarietyStore } from "Store/VarietyStore";
import { formatLengthMeasurement } from "Helpers/UnitFormatter";

import type PlantModel from "Models/Plant";
import type PlantingModel from "Models/Planting";
import SearchableReactDropdown from "./SearchableReactDropdown";

type Props = {
  showCreateModal?: boolean;
  showPlantingInfoModal?: boolean;
  setShowPlantingInfoModal: Dispatch<SetStateAction<boolean>>;
  setShowPlantSelectModal: Dispatch<SetStateAction<boolean>>;
  showPlantSelectModal?: boolean;
  setShowCreateModal: Dispatch<SetStateAction<boolean>>;
  index: number;
};

const PlantingModal: React.FC<Props> = ({
  showCreateModal,
  showPlantingInfoModal,
  setShowPlantSelectModal,
  setShowPlantingInfoModal,
  showPlantSelectModal,
  setShowCreateModal,
  index,
}) => {
  const plants = usePlantStore((state) => state.plants);
  const plantsLoaded = usePlantStore((state) => state.plantsLoaded);
  const fetchPlants = usePlantStore((state) => state.fetchPlants);
  const varieties = useVarietyStore((state) => state.varieties);
  const fetchVarieties = useVarietyStore((state) => state.fetchVarieties);
  const createPlantingRequest = usePlantingStore(
    (state) => state.createPlanting,
  );
  const updatePlantingRequest = usePlantingStore(
    (state) => state.updatePlanting,
  );
  const updatePlantingAtIndex = useGerminationTrayStore(
    (state) => state.updatePlantingAtIndex,
  );
  const tray = useGerminationTrayStore((state) => state.currentGerminationTray);
  const plantings = usePlantingStore((state) => state.plantings);
  const plantingId = tray?.plantingIds[index];
  const planting = plantings.find((item) => item._id === plantingId);
  const refreshPlantings = usePlantingStore((state) => state.getPlantings);
  const openPlantModal = usePlantStore((state) => state.openPlantModal);
  const [harvestDate, setHarvestDate] = useState(
    planting?.germinationDate ? new Date(planting?.harvestDate) : new Date(),
  );
  const [plantingDate, setPlantingDate] = useState(
    planting?.plantDate ? new Date(planting?.plantDate) : new Date(),
  );
  const [plantingGerminationDate, setPlantGerminationDate] = useState(
    planting?.germinationDate
      ? new Date(planting?.germinationDate)
      : new Date(),
  );
  const [currentPlanting, setCurrentPlanting] = useState<PlantingModel>(
    planting ?? {
      _id: new ObjectId().toString(),
      plantId: "",
      germinationDate: new Date().toString(),
      plantDate: new Date().toString(),
      harvestDate: new Date().toString(),
    },
  );
  const [currentPlant, setCurrentPlant] = useState<PlantModel | undefined>(
    undefined,
  );
  const [plantModalActive, setPlantModalActive] = useState(false);
  const plantOptions = currentPlant
    ? [
        ...plants.filter((plant) => plant._id !== currentPlant._id),
        currentPlant,
      ]
    : plants;

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const showModal = (): boolean =>
    !!(showCreateModal || showPlantingInfoModal || showPlantSelectModal);

  useEffect(() => {
    if (!showCreateModal && !showPlantingInfoModal && !showPlantSelectModal)
      return;
    void fetchVarieties();
    if (!plantsLoaded) {
      void fetchPlants();
      return;
    }

    if (planting?.plantId) {
      setCurrentPlanting(planting);
      setCurrentPlant(usePlantStore.getState().getPlant(planting.plantId));
    }
  }, [
    showCreateModal,
    showPlantingInfoModal,
    showPlantSelectModal,
    plantsLoaded,
    planting?._id,
    planting?.plantId,
    fetchPlants,
    fetchVarieties,
  ]);

  useEffect(() => {
    if (!currentPlanting.plantId) return;

    const selectedPlant = plants.find(
      (plant) => plant._id === currentPlanting.plantId,
    );
    if (selectedPlant) setCurrentPlant(selectedPlant);
  }, [plants, currentPlanting.plantId]);

  useEffect(() => {
    if (planting) {
      setHarvestDate(new Date(planting.harvestDate));
      setPlantingDate(new Date(planting.plantDate));
      setPlantGerminationDate(new Date(planting.germinationDate));
    }
  }, [planting?.germinationDate, planting?.plantDate, planting?.harvestDate]);

  const hideModal = () => {
    setShowCreateModal(false);
    setShowPlantingInfoModal(false);
    setShowPlantSelectModal(false);
    setPlantModalActive(false);
    usePlantStore.getState().closePlantModal();
  };

  const hideDeleteModal = () => {
    setShowDeleteModal(false);
    clearPlanting();
    hideModal();
  };

  const cancel = () => {
    setCurrentPlant(undefined);
    hideModal();
  };

  // create/update plant helpers removed (unused)
  const updatePlanting = async (): Promise<PlantingModel | undefined> => {
    if (!currentPlanting) {
      return undefined;
    }

    const updatedPlanting = {
      ...currentPlanting,
      harvestDate: harvestDate.toString(),
      plantDate: plantingDate.toString(),
      germinationDate: plantingGerminationDate.toString(),
    };

    setCurrentPlanting(updatedPlanting);
    return updatedPlanting;
  };

  const clearPlanting = () => {
    setCurrentPlanting({
      _id: new ObjectId().toString(),
      plantId: "",
      germinationDate: new Date().toString(),
      plantDate: new Date().toString(),
      harvestDate: new Date().toString(),
    });
    setCurrentPlant(undefined);
  };

  const updatePlant = async (plant: PlantModel | undefined) => {
    setCurrentPlant(plant);

    setCurrentPlanting((previous) => ({
      ...previous,
      plantId: plant?._id ?? "",
    }));
  };

  const handleSave = async () => {
    if (planting && currentPlanting) {
      const updatedPlanting = await updatePlanting();
      if (!updatedPlanting) return;
      await updatePlantingRequest(updatedPlanting);
      await updatePlantingAtIndex(index, updatedPlanting._id);
    } else if (currentPlanting && currentPlanting.plantId) {
      const updatedPlanting = await updatePlanting();
      if (!updatedPlanting) return;
      const newPlanting = {
        ...updatedPlanting,
        _id: new ObjectId().toString(),
      };

      setCurrentPlanting(newPlanting);
      await createPlantingRequest(newPlanting);
      await updatePlantingAtIndex(index, newPlanting._id);
    } else {
      alert("Please select a plant to create a planting");
      return;
    }

    const currentTray =
      useGerminationTrayStore.getState().currentGerminationTray;
    if (currentTray) {
      const plantingIds = currentTray.plantingIds.filter(
        (id): id is string => id !== null,
      );
      await refreshPlantings(plantingIds);
    }
    hideModal();
  };

  return (
    <div>
      <Modal
        style={ModalStyle.defaultStyle}
        ariaHideApp={false}
        isOpen={showModal()}
        onRequestClose={() => {
          cancel();
        }}
      >
        <div className="flex flex-wrap m-50 p-5">
          <h2 className="m-auto p-5 text-4xl font-semibold">
            {showCreateModal ? "Create Planting" : "Update Planting"}
          </h2>
          <div className="germination-date w-full">
            <h3 className="m-auto pb-3 text-lg">Plant</h3>
            <SearchableReactDropdown
              key={currentPlant?._id}
              options={plantOptions}
              getOptionLabel={(plant) => getPlantDisplayName(plant, varieties)}
              getOptionValue={(plant) => plant._id}
              value={currentPlant?._id}
              controlClassName="border rounded px-5 text-lg cursor-pointer mt-[10px]"
              placeholder="Select a plant"
              arrowClassName="py-5"
              menuClassName="border rounded cursor-pointer"
              actionButton={{
                label: "Create Plant",
                onClick: () => {
                  updatePlant(undefined);
                  setPlantModalActive(true);
                  openPlantModal(undefined, true);
                },
              }}
              onChange={(selectedPlant) => {
                if (!selectedPlant) return;
                setCurrentPlant(selectedPlant);
                setCurrentPlanting((previous) => ({
                  ...previous,
                  plantId: selectedPlant._id,
                }));
              }}
            />
          </div>
          {!!currentPlant && (
            <div>
              <div className="plant-info-container py-5 grid grid-cols-2 grid-rows-10 md:grid-cols-4 md:grid-rows-5 w-full gap-2">
                <h5 className="text-lg">Sun Requirement</h5>
                <p className="plant-info">
                  {currentPlant?.shadePreference ?? "Not specified"}
                </p>
                <h5 className="text-lg">Planting Info</h5>
                <p className="plant-info">
                  {currentPlant?.plantingInfo ?? "Not specified"}
                </p>
                <h5 className="text-lg">Companion Plants</h5>
                <p className="plant-info">
                  {currentPlant?.companionPlants
                    ?.map(
                      (plantId) =>
                        getPlantDisplayName(
                          plants.find((plant) => plant._id === plantId),
                          varieties,
                        ) || plantId,
                    )
                    .join(", ") || "Not specified"}
                </p>
                <h5 className="text-lg">Days to Germinate</h5>
                <p className="plant-info">
                  {currentPlant?.daysToGerminate ?? "Not specified"}
                </p>
                <h5 className="text-lg">Days to Maturity</h5>
                <p className="plant-info">
                  {currentPlant?.daysToMaturity ?? "Not specified"}
                </p>
                <h5 className="text-lg">Seed Depth</h5>
                <p className="plant-info">
                  {formatLengthMeasurement(
                    currentPlant?.seedDepth ?? "Not specified",
                    currentPlant?.seedDepthUnit,
                  )}
                </p>
                <h5 className="text-lg">Seed Spacing</h5>
                <p className="plant-info">
                  {formatLengthMeasurement(
                    currentPlant?.seedSpacing ?? "Not specified",
                    currentPlant?.seedSpacingUnit,
                  )}
                </p>
                <h5 className="text-lg pr-10">Light required to germinate</h5>
                <input
                  type="checkbox"
                  checked={currentPlant?.lightRequiredToGerminate}
                  disabled={true}
                />
                <h5 className="text-lg">Temperature Tolerance</h5>
                <p className="plant-info">
                  {currentPlant?.temperatureTolerance ?? "Not specified"}
                </p>
                <h5 className="text-lg">Water Requirement:</h5>
                <p className="plant-info">
                  {currentPlant?.waterRequirement ?? "Not specified"}
                </p>
              </div>
              <div className="flex">
                <button
                  onClick={() => {
                    setPlantModalActive(true);
                    openPlantModal(currentPlant?._id, true);
                  }}
                  className="btn btn-green my-[10px]"
                >
                  Edit Plant
                </button>
              </div>
            </div>
          )}
          <div className="germination-date w-full">
            <h2 className="m-auto pb-2">Germination Date</h2>
            <Calendar
              className="m-auto"
              onChange={(e) => setPlantGerminationDate(e as Date)}
              value={plantingGerminationDate ?? new Date()}
            />
          </div>
          <div className="germination-date w-full">
            <h5 className="m-auto pb-2">Planting Date</h5>
            <Calendar
              className="m-auto"
              onChange={(c) => setPlantingDate(c as Date)}
              value={plantingDate ?? new Date()}
            />
          </div>
          <div className="germination-date w-full">
            <h5 className="m-auto pb-2">Harvest Date</h5>
            <Calendar
              className="m-auto"
              onChange={(d) => setHarvestDate(d as Date)}
              value={harvestDate ?? new Date()}
            />
          </div>
          <div className="flex pt-10 flex-grow">
            <button onClick={() => cancel()} className="btn bg-gray-light mr-5">
              Cancel
            </button>
            <button onClick={() => void handleSave()} className="btn btn-green">
              Save
            </button>
          </div>
          {!!currentPlanting && !!currentPlanting.plantId && (
            <div className="pt-10">
              <button
                className="btn btn-danger"
                onClick={() => {
                  if (currentPlanting) {
                    setShowDeleteModal(true);
                  }
                }}
              >
                Delete Planting
              </button>
            </div>
          )}
        </div>
      </Modal>

      <DeletePlantingModal
        plantingId={currentPlanting._id}
        hideDeleteModal={hideDeleteModal}
        showDeleteModal={showDeleteModal}
        index={index}
        clearPlanting={clearPlanting}
      />

      <PlantModal
        setPlant={updatePlant}
        isActive={plantModalActive}
        onClose={() => setPlantModalActive(false)}
      />
    </div>
  );
};

export default PlantingModal;
